import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye, Plus, Globe, Loader2, Palette } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from '../../utils/axios';
import { useTranslation } from 'react-i18next';
import TemplatePDFGenerator from './TemplatePDFGenerator'; // Import our new component

interface PDFManagementProps {
  menuId: string;
  menuTitle: string;
  currentLanguage: string;
}

interface PDFFile {
  id: number;
  filename?: string;
  language: string;
  createdAt: string;
  size: number;
}

const PDFManagement: React.FC<PDFManagementProps> = ({ menuId, menuTitle, currentLanguage }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [pdfs, setPdfs] = useState<PDFFile[]>([]);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>(['it', 'en', 'es', 'fr', 'de', 'pt', 'ru', 'ja', 'zh', 'ar', 'hi', 'ko', 'nl', 'sv', 'da', 'fi', 'no', 'pl', 'tr', 'he', 'th', 'id', 'vi', 'cs', 'el', 'hu', 'ro', 'sk', 'uk', 'bg', 'hr', 'sr', 'sl', 'lt', 'lv', 'et']);
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);
  const [showTemplateGenerator, setShowTemplateGenerator] = useState(false); // New state for template selector

  useEffect(() => {
    fetchPDFs();
  }, [menuId]);

  const fetchPDFs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/pdf/menu/${menuId}/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Map the response data to match the PDFFile interface
        const mappedPdfs = response.data.data.map((pdf: any) => ({
          id: pdf.id,
          filename: pdf.fileName, // Map fileName to filename
          language: pdf.language,
          createdAt: pdf.createdAt || new Date().toISOString(), // Ensure it's not undefined
          size: pdf.fileSize,
          // Keep original data for reference
          originalData: pdf
        }));
        setPdfs(mappedPdfs);
      }
    } catch (error) {
      console.error('Failed to fetch PDFs:', error);
      toast.error(t('pdf_management.messages.load_error'));
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/menu/${menuId}/generate-pdf`, {
        language: selectedLanguage
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        toast.success(t('pdf_management.messages.generate_success'));
        fetchPDFs(); // Refresh the list
      }
    } catch (error: any) {
      console.error('Failed to generate PDF:', error);
      toast.error(error.response?.data?.message || t('pdf_management.messages.generate_error'));
    } finally {
      setGenerating(false);
    }
  };

  const downloadPDF = async (pdf: PDFFile) => {
    const filename = pdf.filename;
    if (!filename) {
      toast.error(t('pdf_management.messages.filename_missing'));
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/pdf/download/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        validateStatus: () => true
      });

      if (response.status !== 200) {
        const blob = response.data as Blob;
        let message = t('pdf_management.messages.download_error');
        try {
          const text = await blob.text();
          const json = JSON.parse(text);
          message = json.message || json.error || message;
        } catch (_) {}
        toast.error(message);
        return;
      }

      const blob = response.data as Blob;
      if (blob.size === 0) {
        toast.error(t('pdf_management.messages.download_error'));
        return;
      }

      const contentDisposition = response.headers['content-disposition'];
      let downloadName = filename;
      if (contentDisposition) {
        const match = /filename="?([^";\n]+)"?/.exec(contentDisposition);
        if (match) downloadName = match[1].trim();
      }
      if (!downloadName.toLowerCase().endsWith('.pdf')) downloadName += '.pdf';

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadName);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error: any) {
      console.error('Failed to download PDF:', error);
      toast.error(error.response?.data?.message || t('pdf_management.messages.download_error'));
    }
  };

  const viewPDF = (filename: string | undefined) => {
    if (!filename) {
      toast.error(t('pdf_management.messages.filename_missing'));
      console.error('PDF filename is undefined');
      return;
    }
    
    const token = localStorage.getItem('token');
    const baseUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:5000';
    const url = `${baseUrl}/pdf/view/${filename}?token=${token}`;
    window.open(url, '_blank');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7BD5B5]"></div>
        <span className="ml-3 text-gray-600">{t('pdf_management.loading_pdfs')}</span>
      </div>
    );
  }

  // Show template generator if requested
  if (showTemplateGenerator) {
    return (
      <div>
        <div className="mb-4">
          <button
            onClick={() => setShowTemplateGenerator(false)}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-2"
          >
            ← Indietro
          </button>
        </div>
        <TemplatePDFGenerator menuId={menuId} currentLanguage={currentLanguage} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-[#0A0C0B] mb-6 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          {t('pdf_management.title')}
        </h2>

        
          <button
            onClick={() => setShowTemplateGenerator(true)}
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-black flex items-center gap-2"
          >
            <Palette className="w-4 h-4" />
            Seleziona modello e genera PDF
          </button>
      


        {/* PDF List */}
        <div>
          <h3 className="font-medium text-[#0A0C0B] mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {t('pdf_management.generated_pdfs')}
          </h3>
          
          {pdfs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-[#7BD5B5]/30" />
              <p>{t('pdf_management.no_pdfs_yet')}</p>
              <p className="text-sm">{t('pdf_management.generate_first')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('pdf_management.filename')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('pdf_management.language')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('pdf_management.size')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('pdf_management.created')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('pdf_management.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
  {pdfs.map((pdf) => (
    <tr key={pdf.id} className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div 
          className="text-sm font-medium text-[#0A0C0B] hover:text-blue-600 hover:underline cursor-pointer"
          onClick={() => downloadPDF(pdf)}
          title={t('pdf_management.click_to_download')}
        >
          {pdf.filename}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <Globe className="w-4 h-4 mr-2 text-gray-400" />
          <span className="text-sm text-gray-900">
            {pdf.language.toUpperCase()} - {new Intl.DisplayNames(['en'], { type: 'language' }).of(pdf.language) || pdf.language}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatFileSize(pdf.size)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDate(pdf.createdAt)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadPDF(pdf)}
            className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
            title={t('pdf_management.download_pdf')}
            disabled={!pdf.filename}
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  ))}
</tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFManagement;